import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import Pagination from '../../components/Pagination/Pagination';
import panelStyles from '../../styles/adminPanels.module.css';
import axiosClient from '../../api/axiosClient';

interface UserRow {
  id: string; full_name: string; email: string; role: string; is_active: boolean;
  kyc_status: string | null; wallet_address: string | null; referred_by_name: string | null;
  referral_code: string | null; created_at: string; segment: 'account' | 'customer' | 'cancelled' | 'admin';
}

type Tab = 'all' | 'account' | 'customer' | 'cancelled';
const TABS: { key: Tab; label: string; hint: string }[] = [
  { key: 'all', label: 'All', hint: 'Every registered user' },
  { key: 'account', label: 'Only Account', hint: 'Registered, no portal taken yet' },
  { key: 'customer', label: 'Customers', hint: 'Has an active or completed portal investment' },
  { key: 'cancelled', label: 'Cancelled', hint: 'Portal was cancelled and no live portal now' },
];

const STATUS_COLORS: Record<string, string> = {
  pending: '#e8a86a', approved: '#5cb87d', rejected: '#e2534a',
};

const SEG_COLORS: Record<string, string> = { account: '#e8a86a', customer: '#5cb87d', cancelled: '#e2534a', admin: '#8ab4f8' };
const SEG_LABEL: Record<string, string> = { account: 'Only Account', customer: 'Customer', cancelled: 'Cancelled', admin: 'Admin' };

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<Tab>('all');
  const PAGE_SIZE = 10;

  const load = () => {
    axiosClient.get('/admin/users').then(({ data }) => setUsers(data.users)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggleStatus = async (id: string, isActive: boolean) => {
    try {
      await axiosClient.patch(`/admin/users/${id}/status`, { isActive: !isActive });
      toast.success(!isActive ? 'User activated.' : 'User suspended.');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update status.');
    }
  };

  const count = (t: Tab) => (t === 'all' ? users.length : users.filter((u) => u.segment === t).length);

  const filtered = users.filter((u) => (tab === 'all' || u.segment === tab)).filter((u) =>
    !search || u.full_name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminShell>
      <PageHeader title="User Management" subtitle="All registered users — status, KYC, wallet, and referral info. Click a row to open the full profile." action={<AdminWalletBadge />} />

      <div className={panelStyles.page}>
        <div className={panelStyles.filters} style={{ flexWrap: 'wrap', gap: 8 }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              title={t.hint}
              onClick={() => { setTab(t.key); setPage(1); }}
              style={{
                background: tab === t.key ? 'var(--copper)' : 'transparent',
                color: tab === t.key ? '#fff' : 'var(--cream)',
                border: '1px solid ' + (tab === t.key ? 'var(--copper)' : 'var(--line)'),
                borderRadius: 20, padding: '8px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              }}
            >
              {t.label} ({count(t.key)})
            </button>
          ))}
          <input
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{
              maxWidth: 320, background: 'var(--input-bg)', border: '1px solid var(--input-border)',
              color: 'var(--cream)', borderRadius: 10, padding: '10px 14px', fontSize: 13,
            }}
          />
        </div>

        <div className={panelStyles.tableWrap}>
          {loading && <div className={panelStyles.loading}><span className={panelStyles.spinnerLg} /></div>}
          {!loading && filtered.length === 0 && <div className={panelStyles.empty}>No users found.</div>}
          {!loading && filtered.length > 0 && (
            <table className={panelStyles.table}>
              <thead>
                <tr>
                  <th>User</th><th>Email</th><th>Type</th><th>Status</th><th>KYC Status</th>
                  <th>Wallet Address</th><th>Referred By</th><th>Registered</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((u) => (
                  <tr key={u.id}>
                    <td onClick={() => navigate(`/admin/users/${u.id}`)} style={{ cursor: 'pointer' }}>
                      <div className={panelStyles.nameCell}>{u.full_name}</div>
                      <div className={panelStyles.subCell}>{u.role === 'admin' ? 'Admin' : 'User'}</div>
                    </td>
                    <td onClick={() => navigate(`/admin/users/${u.id}`)} style={{ cursor: 'pointer' }}>{u.email}</td>
                    <td>
                      <span className={panelStyles.statusPill} style={{ color: SEG_COLORS[u.segment], borderColor: SEG_COLORS[u.segment] }}>
                        {SEG_LABEL[u.segment]}
                      </span>
                    </td>
                    <td>
                      <span className={panelStyles.statusPill} style={{ color: u.is_active ? '#5cb87d' : '#e2534a', borderColor: u.is_active ? '#5cb87d' : '#e2534a' }}>
                        {u.is_active ? 'Active' : 'Suspended'}
                      </span>
                    </td>
                    <td>
                      {u.kyc_status ? (
                        <span className={panelStyles.statusPill} style={{ color: STATUS_COLORS[u.kyc_status], borderColor: STATUS_COLORS[u.kyc_status] }}>{u.kyc_status}</span>
                      ) : <span className={panelStyles.subCell}>Not submitted</span>}
                    </td>
                    <td className={panelStyles.subCell} style={{ fontFamily: 'monospace', fontSize: 11 }}>
                      {u.wallet_address ? `${u.wallet_address.slice(0, 6)}…${u.wallet_address.slice(-4)}` : '—'}
                    </td>
                    <td className={panelStyles.subCell}>{u.referred_by_name || '—'}</td>
                    <td className={panelStyles.subCell}>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td>
                      <button className={panelStyles.actionBtn} onClick={() => toggleStatus(u.id, u.is_active)}>
                        {u.is_active ? 'Suspend' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && filtered.length > 0 && (
            <Pagination page={page} totalPages={Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))} onChange={setPage} />
          )}
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminUsers;